import { Injectable } from "@angular/core";
import { SqlFilesHelper } from "@calaosoft/osapp-common/sqlite/helpers/sql-files.helper";
import { SqlRequestHelper } from "@calaosoft/osapp-common/sqlite/helpers/sql-request-helper";
import { ESqliteType } from "@calaosoft/osapp-common/sqlite/models/esqlite-type";
import { IBatchedRequest } from "@calaosoft/osapp-common/sqlite/models/ibatched-request";
import { SqlDataSource } from "@calaosoft/osapp-common/sqlite/models/sql-data-source";
import { SqlRequestResult } from "@calaosoft/osapp-common/sqlite/models/sql-request-result";
import { TRequestParam } from "@calaosoft/osapp-common/sqlite/models/trequest-param";
import { ArrayHelper } from "@calaosoft/osapp-common/utils/helpers/arrayHelper";
import { TupleHelper } from "@calaosoft/osapp-common/utils/helpers/tuple.helper";
import { IKeyValue } from "@calaosoft/osapp-common/utils/models/ikey-value";
import { Tuple } from "@calaosoft/osapp-common/utils/types/tuple";
import { ITaskDescriptor as IOldTaskDescriptor } from "../../../services/backgroundTask/itask-descriptor";
import { FlagService } from "../../../services/flag.service";
import { SqlService } from "../../sqlite/services/sql.service";
import { ISerializableTaskDescriptor } from "../models/iserializable-task-descriptor";
import { ITaskDescriptor } from "../models/itask-descriptor";
import { BackgroundTaskDatabaseService } from "./background-task-database.service";

type TSqliteTuple = Tuple<keyof ISerializableTaskDescriptor, TRequestParam>;
type TExcludePrimaryKeys = Exclude<
	keyof ISerializableTaskDescriptor,
	(typeof MobileBackgroundTaskDatabaseService)["C_DESCRIPTOR_TABLE_PRIMARY_KEY"]
>;

@Injectable()
export class MobileBackgroundTaskDatabaseService extends BackgroundTaskDatabaseService<SqlDataSource> {
	//#region FIELDS

	private static readonly C_DEFAULT_DATABASE_VERSION = 1;

	protected static override readonly C_LOG_ID = "MBL.BTS.DB.S::";

	//#endregion FIELDS

	//#region METHODS

	public constructor(psvcSql: SqlService, psvcFlag: FlagService) {
		super(psvcSql, psvcFlag);
	}

	/** @override */
	public override async compactAsync(): Promise<void> {
		const loDataSource: SqlDataSource = await this.moDatabaseAsync;

		await this.isvcSql.requestAsync(loDataSource, SqlRequestHelper.getVacuumRequest());
	}

	//#region Init

	/** @override */
	protected override async initAsync(): Promise<void> {
		const loDataSource = new SqlDataSource(
			MobileBackgroundTaskDatabaseService.C_DATABASE_ID,
			MobileBackgroundTaskDatabaseService.C_DEFAULT_DATABASE_VERSION,
			this.getSqlDataSourcePath(),
			true
		);

		await this.isvcSql.openDatabaseAsync(loDataSource);
		await this.createTablesAsync(loDataSource);

		this.raiseDatabaseEvent(loDataSource);
	}

	/** @override */
	public override async migrateSchemaAsync(): Promise<void> {
		const loDataSource: SqlDataSource = await this.moDatabaseAsync;
		const loResult: SqlRequestResult<{ name: string }> = await this.isvcSql.requestAsync<{ name: string }>(
			loDataSource,
			`PRAGMA table_info(${MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME})`
		);
		const laExistingColumnNames: string[] = loResult.results?.map((poRow: { name: string }) => poRow.name) ?? [];
		const laMissingKeys: Array<keyof ISerializableTaskDescriptor> =
			MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_KEYS.filter(
				(psKey: keyof ISerializableTaskDescriptor) => !laExistingColumnNames.includes(psKey)
			);

		for (let lnIndex = 0; lnIndex < laMissingKeys.length; lnIndex++) {
			const lsKey: keyof ISerializableTaskDescriptor = laMissingKeys[lnIndex];
			console.info(
				`${MobileBackgroundTaskDatabaseService.C_LOG_ID}Migrating column '${lsKey}' in table '${MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME}'.`
			);
			await this.isvcSql.requestAsync(
				loDataSource,
				`ALTER TABLE ${MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME} ADD COLUMN ${lsKey} ${MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TYPES[lsKey]}`
			);
		}
	}

	private getSqlDataSourcePath(): string {
		return SqlFilesHelper.getFileName(
			MobileBackgroundTaskDatabaseService.C_DATABASE_ID,
			MobileBackgroundTaskDatabaseService.C_DEFAULT_DATABASE_VERSION
		);
	}

	private async createTablesAsync(poDataSource: SqlDataSource): Promise<void> {
		const laRequests: string[] = [
			SqlRequestHelper.getCreateTableRequest<ISerializableTaskDescriptor>(
				MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME,
				[MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_PRIMARY_KEY],
				this.descriptorTypesToKeyValues()
			)
		];

		await this.isvcSql.runTransactionAsync(poDataSource, laRequests, [], poDataSource.databaseId);
	}

	private descriptorTypesToKeyValues(): Array<IKeyValue<keyof ISerializableTaskDescriptor, ESqliteType>> {
		return MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_KEYS.map(
			(psKey: keyof ISerializableTaskDescriptor): IKeyValue<keyof ISerializableTaskDescriptor, ESqliteType> => {
				return {
					key: psKey,
					value: MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TYPES[psKey]
				};
			}
		);
	}

	//#endregion Init

	//#region Put

	/** @override */
	public override async putAsync(poDocument: ITaskDescriptor | IOldTaskDescriptor): Promise<void> {
		const loDataSource: SqlDataSource = await this.moDatabaseAsync;
		const loSqliteDescriptor: ISerializableTaskDescriptor = this.toSerializableTaskDescriptor(poDocument);
		const laSqliteTuples: TSqliteTuple[] = this.getValidSqliteTaskDescriptorTuples(loSqliteDescriptor);

		console.info(
			`${MobileBackgroundTaskDatabaseService.C_LOG_ID}Put descriptor '${loSqliteDescriptor.uid}' (${loSqliteDescriptor.description}) begins`
		);

		await this.isvcSql.runTransactionAsync(
			loDataSource,
			this.getPutInsertRequests(loSqliteDescriptor, laSqliteTuples),
			[TupleHelper.getSecondElements(laSqliteTuples)]
		);

		console.info(
			`${MobileBackgroundTaskDatabaseService.C_LOG_ID}Put descriptor '${loSqliteDescriptor.uid}' (${loSqliteDescriptor.description}) ended`
		);
	}

	private getValidSqliteTaskDescriptorTuples(poDescriptor: ISerializableTaskDescriptor): TSqliteTuple[] {
		return TupleHelper.filterDefinedTuples(
			MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_KEYS.map(
				(psKey: keyof ISerializableTaskDescriptor): TSqliteTuple => [psKey, poDescriptor[psKey]]
			)
		);
	}

	private getPutInsertRequests(
		poSqliteDescriptor: ISerializableTaskDescriptor,
		paSqliteTuples: TSqliteTuple[]
	): string[] {
		const laValidKeys: Array<keyof ISerializableTaskDescriptor> = TupleHelper.getFirstElements(paSqliteTuples);

		// On crée les requêtes d'insertion en mettant à jour automatiquement les colonnes non primaires en cas de conflit.
		const lsOnConflitRequest: string = SqlRequestHelper.getOnConflictUpdateRequest<ISerializableTaskDescriptor>(
			[MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_PRIMARY_KEY],
			this.getNotPrimaryKeys(laValidKeys)
		);

		return SqlRequestHelper.getInsertRequest<ISerializableTaskDescriptor>(
			MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME,
			laValidKeys,
			[poSqliteDescriptor]
		).map((poRequest: IBatchedRequest<ISerializableTaskDescriptor>) => `${poRequest.request} ${lsOnConflitRequest}`);
	}

	private getNotPrimaryKeys(paKeys: Array<keyof ISerializableTaskDescriptor>): TExcludePrimaryKeys[] {
		return paKeys.filter(
			(psKey: keyof ISerializableTaskDescriptor): psKey is TExcludePrimaryKeys =>
				psKey !== MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_PRIMARY_KEY
		);
	}

	//#endregion Put

	//#region Get

	/** @override */
	public async getAsync(psId: string): Promise<ITaskDescriptor | undefined> {
		const loDataSource: SqlDataSource = await this.moDatabaseAsync;

		const loResult: SqlRequestResult<ISerializableTaskDescriptor> = await this.isvcSql.requestAsync(
			loDataSource,
			this.getGetRequest(psId),
			[psId]
		);

		return loResult.hasResults() ? this.toTaskDescriptor(loResult.getFirstResult()!) : undefined;
	}

	private getGetRequest(psId: string): string {
		return SqlRequestHelper.getBy<ISerializableTaskDescriptor>(
			MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME,
			MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_PRIMARY_KEY,
			psId
		);
	}

	/** @override */
	public override async getAllAsync(): Promise<ITaskDescriptor[]> {
		const loDataSource: SqlDataSource = await this.moDatabaseAsync;

		const loRequestResult: SqlRequestResult<ISerializableTaskDescriptor> =
			await this.isvcSql.requestAsync<ISerializableTaskDescriptor>(
				loDataSource,
				SqlRequestHelper.selectAllFromTableRequest(MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME)
			);

		return loRequestResult.results
			? loRequestResult.results.map((poResult: ISerializableTaskDescriptor) => this.toTaskDescriptor(poResult))
			: [];
	}

	//#endregion Get

	//#region Delete

	/** @override */
	public override deleteAsync(psId: string): Promise<void>;
	/** @override */
	public override deleteAsync(poDescriptor: ITaskDescriptor | IOldTaskDescriptor): Promise<void>;
	public override async deleteAsync(poData: string | (ITaskDescriptor | IOldTaskDescriptor)): Promise<void> {
		const loDataSource: SqlDataSource = await this.moDatabaseAsync;
		const lsDescriptorId: string =
			typeof poData === "string" ? poData : ((poData as ITaskDescriptor).uid ?? (poData as IOldTaskDescriptor)._id);

		console.info(`${MobileBackgroundTaskDatabaseService.C_LOG_ID}Delete descriptor '${lsDescriptorId}' begins`);

		await this.isvcSql.runTransactionAsync(loDataSource, [this.getDeleteRequest(lsDescriptorId)], [[lsDescriptorId]]);

		console.info(`${MobileBackgroundTaskDatabaseService.C_LOG_ID}Delete descriptor '${lsDescriptorId}' ended`);
	}

	private getDeleteRequest(psId: string): string;
	private getDeleteRequest(paIds: string[]): string;
	private getDeleteRequest(poData: string | string[]): string {
		return SqlRequestHelper.getDeleteRequestByIds<ISerializableTaskDescriptor>(
			MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME,
			poData instanceof Array ? poData : [poData],
			MobileBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_PRIMARY_KEY
		);
	}

	public override deleteMultipleAsync(paIds: string[]): Promise<void>;
	public override deleteMultipleAsync(paDescriptors: ITaskDescriptor[] | IOldTaskDescriptor[]): Promise<void>;
	public override async deleteMultipleAsync(
		paData: string[] | (ITaskDescriptor[] | IOldTaskDescriptor[])
	): Promise<void> {
		const loDataSource: SqlDataSource = await this.moDatabaseAsync;
		const laIds: string[] = this.getDescriptorIds(paData);
		const laJoinedIds: string = laIds.join(", ");

		console.info(`${MobileBackgroundTaskDatabaseService.C_LOG_ID}Delete descriptors '[${laJoinedIds}]' begins`);

		await this.isvcSql.runTransactionAsync(loDataSource, [this.getDeleteRequest(laIds)], [laIds]);

		console.info(`${MobileBackgroundTaskDatabaseService.C_LOG_ID}Delete descriptors '[${laJoinedIds}]' ended`);
	}

	private getDescriptorIds(paData: string[] | (ITaskDescriptor[] | IOldTaskDescriptor[])): string[] {
		if (typeof ArrayHelper.getFirstElement(paData as string[]) === "string") return paData as string[];
		else {
			return (paData as ITaskDescriptor[] | IOldTaskDescriptor[]).map(
				(poDescriptor: ITaskDescriptor | IOldTaskDescriptor): string =>
					(poDescriptor as ITaskDescriptor).uid ?? (poDescriptor as IOldTaskDescriptor)._id
			);
		}
	}

	//#endregion Delete

	//#endregion METHODS
}
